// src/components/sub-bar.js — Sub-bar global V2-Topbar.2 (fiel ao monolito).
//
// API:
//   setSubBar({ icon, modulo, pagina, id, actions })
//     icon    · SVG string ou ID do sprite (módulo)
//     modulo  · uppercase label (ex "INTELIGÊNCIA", "EQUIPE", "OPERAÇÕES")
//     pagina  · título da página (ex "Dashboard Operação", "Funcionários")
//     id      · código (ex "BI-001", "RH-007", "FIN-CAIXA")
//     actions · array [{ label, onClick, primary, icon }]
//   clearSubBar()
//
// Comportamento:
//   - Auto-hide se sem marker E sem actions
//   - Mountada uma única vez em mountShell()
//   - Cada módulo chama setSubBar no render() — substitui o anterior
//   - Botões: secundário (border + tonal) + primário (gradient violet→cyan)

import './sub-bar.css';
import { getTela } from '../config/telas.js';
import { getCurrentRoute } from '../router/router.js';

let _container = null;

export function ensureSubBar() {
  if (_container && document.body.contains(_container)) return _container;
  // Insere a sub-bar como filho direto do main-content ou do app-main
  const main = document.getElementById('main-content');
  if (!main) return null;
  let sb = document.getElementById('sub-bar');
  if (!sb) {
    sb = document.createElement('div');
    sb.id = 'sub-bar';
    sb.className = 'sub-bar';
    main.parentElement.insertBefore(sb, main);
  }
  _container = sb;
  return sb;
}

/**
 * Contexto da tela para quem precisa saber "onde o usuário estava" — hoje só o
 * helpdesk (modules/helpdesk/novoChamado.js, campo "Onde aconteceu").
 *
 * A fonte é o registro (config/telas.js), resolvido pela ROTA. Se o módulo passar
 * modulo/pagina/id explícitos (telas dinâmicas: Gantt, detalhe de orçamento…), eles
 * têm precedência — o router chama clearSubBar() a cada navegação, então esse
 * override nunca vaza para a tela seguinte.
 */
function _publicarCtx({ modulo, pagina, id } = {}) {
  let base = null;
  try { base = getTela(getCurrentRoute().page); } catch (e) {}
  try {
    window._subBarCtx = {
      id:     id     || base?.id     || null,
      pagina: pagina || base?.pagina || null,
      modulo: modulo || base?.modulo || null,
    };
  } catch (e) {}
}

export function setSubBar({ icon, modulo, pagina, id, actions } = {}) {
  _publicarCtx({ modulo, pagina, id });
  const sb = ensureSubBar();
  if (!sb) return;
  const hasMarker = !!(modulo || pagina || id);
  const hasActions = !!(actions && actions.length);

  if (!hasMarker && !hasActions) {
    sb.classList.remove('has-content');
    sb.innerHTML = '';
    return;
  }

  const iconHTML = icon || '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="6" cy="6" r="2"/><circle cx="18" cy="6" r="2"/><circle cx="6" cy="18" r="2"/><circle cx="18" cy="18" r="2"/></svg>';

  let html = '';
  if (hasMarker) {
    html += `<div class="tm-cap">`;
    if (modulo) {
      html += `<div class="tm-mod"><span class="tm-mod-ic">${iconHTML}</span><span>${_esc(modulo)}</span></div>`;
    }
    if (pagina) {
      html += `<div class="tm-title">${_esc(pagina)}</div>`;
    }
    if (id) {
      html += `<div class="tm-id">${_esc(id)}</div>`;
    }
    html += `</div>`;
  }
  if (hasActions) {
    html += `<div class="tm-actions">`;
    actions.forEach((act, i) => {
      const cls = (act.primary ? 'tm-btn primary' : 'tm-btn') + (act.cls ? ' ' + act.cls : '');
      const ic = act.icon ? `<span class="tm-btn-ic">${act.icon}</span>` : '';
      html += `<button class="${cls}" data-tm-act="${i}">${ic}<span>${_esc(act.label)}</span></button>`;
    });
    html += `</div>`;
  }
  sb.innerHTML = html;
  sb.classList.add('has-content');

  // Wire actions
  if (hasActions) {
    sb.querySelectorAll('[data-tm-act]').forEach((btn) => {
      const i = Number(btn.dataset.tmAct);
      btn.addEventListener('click', () => actions[i]?.onClick?.());
    });
  }
}

export function clearSubBar() {
  _publicarCtx();
  setSubBar({});
}

function _esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
